/**
 * ResumeEmailForm Component
 *
 * Status: [IMPLEMENTED]
 * Task: Resume Request Email Notification
 *
 * Shows an email input form when the bot asks for email
 * to send Raja's resume.
 */

import React, { useState } from 'react';
import { createLogger } from '../../utils/logger';

const logger = createLogger('RESUME');

const ResumeEmailForm = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState(''); // Honeypot field
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      setStatus('error');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    logger.info('Submitting resume request', { email: email.substring(0, 3) + '***' });

    try {
      const response = await fetch('/api/resume-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || null,
          website: website || null, // Honeypot - should be empty
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.error || 'Failed to submit request');
      }

      logger.info('Resume request submitted successfully');
      setStatus('success');
    } catch (err) {
      logger.error('Resume request failed', { error: err.message });
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  // Success state
  if (status === 'success') {
    return (
      <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
        <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">Request received!</span>
        </div>
        <p className="mt-1 text-sm text-green-600 dark:text-green-400">
          Raja will send his resume to {email} shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600">
      <div className="space-y-3">
        {/* Honeypot field - hidden from humans, bots will fill it */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Name input (optional) */}
        <div>
          <label htmlFor="resume-name" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
            Your Name (optional)
          </label>
          <input
            id="resume-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            disabled={status === 'loading'}
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-500 rounded-md
                       bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                       placeholder-slate-400 dark:placeholder-slate-400
                       focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent
                       disabled:opacity-50"
          />
        </div>

        {/* Email input */}
        <div>
          <label htmlFor="resume-email" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
            Your Email <span className="text-red-500">*</span>
          </label>
          <input
            id="resume-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === 'error') setStatus('idle');
            }}
            placeholder="you@example.com"
            required
            disabled={status === 'loading'}
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-500 rounded-md
                       bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                       placeholder-slate-400 dark:placeholder-slate-400
                       focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent
                       disabled:opacity-50"
          />
        </div>

        {/* Error message */}
        {status === 'error' && errorMessage && (
          <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full px-4 py-2 bg-sky-600 hover:bg-sky-700 dark:bg-sky-700 dark:hover:bg-sky-600
                     text-white text-sm font-medium rounded-md transition-colors
                     focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2"
        >
          {status === 'loading' ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Request Resume
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ResumeEmailForm;
